import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Mail, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const VerificationRequiredModal = ({ isOpen, onClose, userEmail }) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResendEmail = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: userEmail });
      setSent(true);
      toast.success(t('verification.emailSent'));
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail === "Email already verified") {
        toast.success(t('profile.emailVerified'));
        onClose();
        window.location.reload();
      } else {
        toast.error(error.response?.data?.detail || t('errors.generic'));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">{t('verification.required')}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-left pt-2">
            {t('verification.pleaseVerify')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-slate-900">{t('verification.whyNeeded')}</h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('verification.reason1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('verification.reason2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('verification.reason3')}</span>
              </li>
            </ul>
          </div>

          {sent ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="font-medium text-green-800">{t('verification.emailSent')}</p>
              <p className="text-sm text-green-600 mt-1">
                {t('verification.checkInbox')} <strong>{userEmail}</strong>
              </p>
              <p className="text-xs text-green-600 mt-2">
                {t('verification.checkSpam')}
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <Mail className="w-4 h-4 inline mr-1" />
                {t('verification.alreadySent')} <strong>{userEmail}</strong> {t('verification.whenRegistered')}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleResendEmail}
            disabled={sending}
            className="flex-1 bg-[#4F46E5] hover:bg-[#4338CA]"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                {t('verification.resendEmail')}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationRequiredModal;
